import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSeoTags, buildWebPageJsonLd } from '../hooks/useSeoTags';
import '../components/cefr/CEFRGuideModal.css';
import './CEFRGuidePage.css';

const CEFR_LEVELS = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'] as const;

const CEFRGuidePage: React.FC = () => {
  const { t } = useTranslation('cefrGuide');

  const renderCell = (value: string) => {
    if (value === '—' || value === '') {
      return <span className="cefr-guide-dash">—</span>;
    }
    return value;
  };

  // SEO for /cefr-guide — an explainer page that links from every course
  // detail/list card. WebPage + BreadcrumbList; high-value SEO target
  // because "CEFR A1 A2 B1 ..." is a heavily searched query in our markets.
  const cefrTitle = t('title');
  const cefrDescription = t('intro');
  const seoTags = useSeoTags({
    title: cefrTitle,
    description: cefrDescription,
    canonical: '/cefr-guide',
    jsonLd: buildWebPageJsonLd({
      url: '/cefr-guide',
      name: cefrTitle,
      description: cefrDescription,
      breadcrumbs: [
        { name: 'Loopy', url: '/' },
        { name: cefrTitle, url: '/cefr-guide' },
      ],
    }),
  });

  return (
    <div className="cefr-guide-page">
      {seoTags}
      <div className="cefr-guide-page__container">
        <header className="cefr-guide-page__header">
          <h1 className="cefr-guide-page__title">{t('title')}</h1>
          <p className="cefr-guide-page__intro">{t('intro')}</p>
        </header>

        <section className="cefr-guide-page__section">
          <table className="cefr-guide-table">
            <thead>
              <tr>
                <th>{t('tableHeaders.level')}</th>
                <th>{t('tableHeaders.domestic')}</th>
                <th>{t('tableHeaders.school')}</th>
                <th>{t('tableHeaders.toeic')}</th>
                <th>{t('tableHeaders.ielts')}</th>
              </tr>
            </thead>
            <tbody>
              {CEFR_LEVELS.map((level) => (
                <tr key={level} id={`level-${level}`}>
                  <td>
                    <div className="cefr-guide-level-cell">
                      <span className={`cefr-guide-badge cefr-guide-badge--${level}`}>{level}</span>
                      <span className="cefr-guide-level-name">{t(`levels.${level}.name`)}</span>
                    </div>
                  </td>
                  <td>{renderCell(t(`levels.${level}.domestic`))}</td>
                  <td>{renderCell(t(`levels.${level}.school`))}</td>
                  <td>{renderCell(t(`levels.${level}.toeic`))}</td>
                  <td>{renderCell(t(`levels.${level}.ielts`))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        <section className="cefr-guide-page__descriptions">
          {CEFR_LEVELS.map((level) => (
            <article key={level} className="cefr-guide-page__level-card" id={level}>
              <div className="cefr-guide-level-cell">
                <span className={`cefr-guide-badge cefr-guide-badge--${level}`}>{level}</span>
                <span className="cefr-guide-page__level-name">{t(`levels.${level}.name`)}</span>
              </div>
              <p className="cefr-guide-page__level-desc">{t(`levels.${level}.description`)}</p>
            </article>
          ))}
        </section>
      </div>
    </div>
  );
};

export default CEFRGuidePage;
